/**
 * Diacritic-insensitive, case-insensitive substring search used by SelectBox find filtering,
 * plus the culture-aware sort those option lists are shown in.
 */

import {currentCulture} from "@2d-front-monorepo/core";

/** Lowercases and strips combining marks, so "José" folds to "jose". */
export function foldText(text: string): string {
	return text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

/** True when `text` contains `query`, ignoring case and diacritics. An empty query matches everything. */
export function textMatches(text: string, query: string): boolean {
	return foldText(text).includes(foldText(query));
}

export interface MatchRange {
	start: number;
	end: number;
}

/**
 * Locates `query` inside `text` (folded comparison) and returns the range in the ORIGINAL string,
 * so the match can be highlighted without losing accents. Null when there is no match or no query.
 */
export function findMatchRange(text: string, query: string): MatchRange | null {
	const foldedQuery = foldText(query);

	if (foldedQuery.length === 0) {
		return null;
	}

	// Fold per code point, keeping a folded-index -> original-index map (folding can change lengths: é -> e).
	let folded = "";
	const map: number[] = [];
	let originalIndex = 0;

	for (const char of text) {
		const foldedChar = foldText(char);

		for (let i = 0; i < foldedChar.length; i++) {
			folded += foldedChar[i];
			map.push(originalIndex);
		}

		originalIndex += char.length;
	}

	const index = folded.indexOf(foldedQuery);

	if (index < 0) {
		return null;
	}

	const lastOriginal = map[index + foldedQuery.length - 1];

	return {start: map[index], end: lastOriginal + 1 + (text.codePointAt(lastOriginal)! > 0xffff ? 1 : 0)};
}

/** Sorts by a derived text using the current culture's collator, leaving the input untouched. */
export function sortByText<T>(items: readonly T[], text: (item: T) => string): T[] {
	const {compare} = currentCulture().getCollator("sort");
	return [...items].sort((a, b) => compare(text(a), text(b)));
}
